import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ROUND_STATES } from "../../../util/Constants";
import type { TournamentRound } from "../../../types/domain";

interface RoundTimerProps {
  round: TournamentRound;
}

function formatDuration(ms: number): string {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const pad = (n: number) => n.toString().padStart(2, "0");
  return hours > 0 ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${minutes}:${pad(seconds)}`;
}

/**
 * Shows how long a round is taking (live, ticking) while in progress, or how long it took once
 * finished. Start is the round's createdAt; end is finishedAt (falling back to updatedAt for
 * legacy rounds that predate the finishedAt column).
 */
function RoundTimer({ round }: RoundTimerProps) {
  const { t } = useTranslation();
  const isRunning = round.state === ROUND_STATES.IN_PROGRESS;

  // Re-render every second only while running.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!isRunning) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [isRunning]);

  const start = new Date(round.createdAt).getTime();
  if (Number.isNaN(start)) return null;

  const end = isRunning
    ? now
    : new Date(round.finishedAt ?? round.updatedAt).getTime();
  const elapsed = formatDuration(end - start);

  return (
    <div className={"round-timer" + (isRunning ? " round-timer-running" : "")}>
      {isRunning && <span className="round-timer-dot" aria-hidden="true" />}
      <span className="round-timer-label">
        {isRunning ? t("roundDurationRunning") : t("roundDurationFinished")}
      </span>
      <span className="round-timer-value">{elapsed}</span>
    </div>
  );
}

export default RoundTimer;
